import React, { useEffect, useState, useCallback } from "react";
import { api, fmtDate } from "@finance/lib/api";
import { toast } from "sonner";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { DateField } from "@finance/components/DateField";
import { Label } from "@finance/components/ui/label";
import { Textarea } from "@finance/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@finance/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { Plus, PencilSimple, Trash, DownloadSimple } from "@phosphor-icons/react";

const STATUSES = ["Not Started", "In Progress", "Complete"];

function riskDeadline(deadline) {
  if (!deadline) return false;
  const d = new Date(deadline);
  const soon = new Date();
  soon.setDate(soon.getDate() + 7);
  return d <= soon;
}

export default function ExecTrackerTable() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);

  const load = useCallback(() => { api.get("/exec/tracker").then((r) => setRows(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, [load]);

  const start = (row, category) => {
    setF(row ? { ...row } : { category: category || "", task_description: "", action_plan: "", assigned_to: "", deadline: "", percent_completed: 0, status: "Not Started", comment: "" });
    setOpen(true);
  };
  const save = async () => {
    if (!f.category || !f.task_description) { toast.error("Category and task description are required."); return; }
    const body = { category: f.category, task_description: f.task_description, action_plan: f.action_plan || null,
      assigned_to: f.assigned_to || null, deadline: f.deadline || null, percent_completed: Number(f.percent_completed) || 0,
      status: f.status, comment: f.comment || null, order: f.order || 0 };
    try {
      if (f.id) await api.put(`/exec/tracker/${f.id}`, body); else await api.post("/exec/tracker", body);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const patch = async (row, changes) => {
    const body = { ...row, ...changes };
    try { await api.put(`/exec/tracker/${row.id}`, body); load(); } catch (e) { toast.error(e.response?.data?.detail); }
  };
  const del = async (row) => { if (!window.confirm("Delete this row?")) return; try { await api.delete(`/exec/tracker/${row.id}`); load(); } catch (e) { toast.error(e.response?.data?.detail); } };

  // group by category, preserving order
  const groups = [];
  rows.forEach((r) => {
    let g = groups.find((x) => x.category === r.category);
    if (!g) { g = { category: r.category, items: [] }; groups.push(g); }
    g.items.push(r);
  });

  const uname = (id) => users.find((u) => u.id === id)?.name || "—";

  const exportExcel = () => {
    import("xlsx").then((XLSX) => {
      const data = rows.map((r) => ({
        Category: r.category, "Task Description": r.task_description, "Action Plan": r.action_plan || "",
        "Assigned To": uname(r.assigned_to), Deadline: r.deadline ? fmtDate(r.deadline) : "",
        "% Completed": r.percent_completed, Status: r.status, Comment: r.comment || "",
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Executive Tracker");
      XLSX.writeFile(wb, "executive-tracker.xlsx");
    });
  };

  return (
    <div data-testid="exec-tracker-table">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs text-gray-500">Executive-only structured tracker. Not linked to My Actions / All Actions.</p>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="tracker-export-excel" onClick={exportExcel}><DownloadSimple size={16} className="mr-1" />Export to Excel</Button>
          <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="tracker-add-row" onClick={() => start(null)}><Plus size={16} className="mr-1" />Add Task / Category</Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-sm border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
            <th className="px-3 py-2 text-left">Category</th><th className="px-3 py-2 text-left">Task Description</th>
            <th className="px-3 py-2 text-left">Action Plan</th><th className="px-3 py-2 text-left">Assigned To</th>
            <th className="px-3 py-2 text-left">Deadline</th><th className="px-3 py-2 text-left">% Completed</th>
            <th className="px-3 py-2 text-left">Status Update</th><th className="px-3 py-2 text-left">Comment</th><th className="px-3 py-2"></th>
          </tr></thead>
          <tbody>
            {groups.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400" data-testid="tracker-empty">No executive items yet. Add your first task or category.</td></tr>}
            {groups.map((g) => g.items.map((r, idx) => {
              const complete = r.status === "Complete" || Number(r.percent_completed) >= 100;
              const risk = r.status === "Not Started" && riskDeadline(r.deadline);
              const cellTone = complete ? "bg-emerald-50 text-emerald-700" : risk ? "bg-red-50 text-red-700" : "";
              return (
                <tr key={r.id} data-testid={`tracker-row-${r.id}`} className="border-b border-gray-100 align-top">
                  {idx === 0 && (
                    <td rowSpan={g.items.length} className="border-r border-gray-100 bg-gray-50/60 px-3 py-2 font-medium align-middle" data-testid={`tracker-cat-${g.category}`}>
                      {g.category}
                      <button className="mt-2 block text-xs text-[#1B4383] hover:underline" data-testid={`tracker-addto-${g.category}`} onClick={() => start(null, g.category)}>+ add task</button>
                    </td>
                  )}
                  <td className="px-3 py-2">{r.task_description}</td>
                  <td className="px-3 py-2 text-gray-600">{r.action_plan || "—"}</td>
                  <td className="px-3 py-2 text-gray-600">{uname(r.assigned_to)}</td>
                  <td className={`px-3 py-2 lx-num ${cellTone}`} data-testid={`tracker-deadline-${r.id}`}>{r.deadline ? fmtDate(r.deadline) : "—"}</td>
                  <td className="px-3 py-2">
                    <Input type="number" min={0} max={100} className="h-8 w-20 text-right text-sm" data-testid={`tracker-pct-${r.id}`}
                      value={r.percent_completed} onChange={(e) => setRows((s) => s.map((x) => x.id === r.id ? { ...x, percent_completed: e.target.value } : x))}
                      onBlur={(e) => patch(r, { percent_completed: Number(e.target.value) || 0 })} />
                  </td>
                  <td className={`px-3 py-2 ${cellTone}`} data-testid={`tracker-status-${r.id}`}>
                    <Select value={r.status} onValueChange={(v) => patch(r, { status: v })}>
                      <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{r.comment || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    <button className="mr-3 text-gray-400 hover:text-[#1B4383]" data-testid={`tracker-edit-${r.id}`} onClick={() => start(r)}><PencilSimple size={16} /></button>
                    <button className="text-gray-400 hover:text-red-500" data-testid={`tracker-del-${r.id}`} onClick={() => del(r)}><Trash size={15} /></button>
                  </td>
                </tr>
              );
            }))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="tracker-dialog">
          <DialogHeader><DialogTitle className="font-display">{f?.id ? "Edit" : "New"} Executive Task</DialogTitle>
            <DialogDescription>Executive-only. Group tasks by giving them the same Category.</DialogDescription></DialogHeader>
          {f && (
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="lx-label">Category</Label><Input className="mt-1" data-testid="tracker-f-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} /></div>
              <div><Label className="lx-label">Assigned To</Label>
                <Select value={f.assigned_to || "none"} onValueChange={(v) => setF({ ...f, assigned_to: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1" data-testid="tracker-f-assigned"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="col-span-2"><Label className="lx-label">Task Description</Label><Input className="mt-1" data-testid="tracker-f-task" value={f.task_description} onChange={(e) => setF({ ...f, task_description: e.target.value })} /></div>
              <div className="col-span-2"><Label className="lx-label">Action Plan</Label><Textarea rows={2} className="mt-1" data-testid="tracker-f-plan" value={f.action_plan || ""} onChange={(e) => setF({ ...f, action_plan: e.target.value })} /></div>
              <div><Label className="lx-label">Deadline</Label><DateField className="mt-1" data-testid="tracker-f-deadline" value={(f.deadline || "").slice(0, 10)} onChange={(e) => setF({ ...f, deadline: e.target.value })} /></div>
              <div><Label className="lx-label">% Completed</Label><Input type="number" min={0} max={100} className="mt-1" data-testid="tracker-f-pct" value={f.percent_completed} onChange={(e) => setF({ ...f, percent_completed: e.target.value })} /></div>
              <div><Label className="lx-label">Status</Label>
                <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger className="mt-1" data-testid="tracker-f-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              <div className="col-span-2"><Label className="lx-label">Comment</Label><Input className="mt-1" data-testid="tracker-f-comment" value={f.comment || ""} onChange={(e) => setF({ ...f, comment: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="tracker-save" onClick={save}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
